import { forwardRef, useState, useEffect, InputHTMLAttributes } from 'react';

type MaskType = 'cpf' | 'cnpj' | 'cpfCnpj' | 'phone' | 'cellphone' | 'cep' | 'date' | 'plate';

interface InputMaskProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'value'> {
  value: string | null | undefined;
  onChange: (value: string) => void;
  mask: MaskType;
  label?: string;
  error?: string;
}

// Máscaras disponíveis
const masks: Record<MaskType, { pattern: RegExp; format: (value: string) => string; maxLength: number }> = {
  cpf: {
    pattern: /\d/g,
    format: (v) => {
      const digits = v.replace(/\D/g, '').slice(0, 11);
      return digits
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
    },
    maxLength: 14,
  },
  cnpj: {
    pattern: /\d/g,
    format: (v) => {
      const digits = v.replace(/\D/g, '').slice(0, 14);
      return digits
        .replace(/(\d{2})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1/$2')
        .replace(/(\d{4})(\d{1,2})$/, '$1-$2');
    },
    maxLength: 18,
  },
  cpfCnpj: {
    pattern: /\d/g,
    format: (v) => {
      const digits = v.replace(/\D/g, '');
      if (digits.length <= 11) {
        return digits
          .slice(0, 11)
          .replace(/(\d{3})(\d)/, '$1.$2')
          .replace(/(\d{3})(\d)/, '$1.$2')
          .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
      }
      return digits
        .slice(0, 14)
        .replace(/(\d{2})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1/$2')
        .replace(/(\d{4})(\d{1,2})$/, '$1-$2');
    },
    maxLength: 18,
  },
  phone: {
    pattern: /\d/g,
    format: (v) => {
      const digits = v.replace(/\D/g, '').slice(0, 10);
      return digits
        .replace(/(\d{2})(\d)/, '($1) $2')
        .replace(/(\d{4})(\d{1,4})$/, '$1-$2');
    },
    maxLength: 14,
  },
  cellphone: {
    pattern: /\d/g,
    format: (v) => {
      const digits = v.replace(/\D/g, '').slice(0, 11);
      return digits
        .replace(/(\d{2})(\d)/, '($1) $2')
        .replace(/(\d{5})(\d{1,4})$/, '$1-$2');
    },
    maxLength: 15,
  },
  cep: {
    pattern: /\d/g,
    format: (v) => {
      const digits = v.replace(/\D/g, '').slice(0, 8);
      return digits.replace(/(\d{5})(\d{1,3})$/, '$1-$2');
    },
    maxLength: 9,
  },
  date: {
    pattern: /\d/g,
    format: (v) => {
      const digits = v.replace(/\D/g, '').slice(0, 8);
      return digits
        .replace(/(\d{2})(\d)/, '$1/$2')
        .replace(/(\d{2})(\d)/, '$1/$2');
    },
    maxLength: 10,
  },
  plate: {
    pattern: /[A-Za-z0-9]/g,
    format: (v) => {
      const chars = v.replace(/[^A-Za-z0-9]/g, '').slice(0, 7).toUpperCase();
      // Formato Mercosul: ABC1D23
      if (chars.length > 4) {
        return chars.slice(0, 3) + chars.slice(3, 4) + chars.slice(4);
      }
      return chars;
    },
    maxLength: 7,
  },
};

export const InputMask = forwardRef<HTMLInputElement, InputMaskProps>(
  ({ value, onChange, mask, label, error, className = '', ...props }, ref) => {
    const [displayValue, setDisplayValue] = useState('');
    const maskConfig = masks[mask];

    // Atualizar display quando o valor externo muda
    useEffect(() => {
      if (value === null || value === undefined || value === '') {
        setDisplayValue('');
      } else {
        setDisplayValue(maskConfig.format(value));
      }
    }, [value, maskConfig]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const inputValue = e.target.value;

      if (!inputValue) {
        setDisplayValue('');
        onChange('');
        return;
      }

      const formatted = maskConfig.format(inputValue);
      setDisplayValue(formatted);

      // Retornar apenas os dígitos/caracteres limpos
      const cleanValue = inputValue.replace(/\D/g, '');
      onChange(cleanValue);
    };

    return (
      <div className="w-full">
        {label && (
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {label}
          </label>
        )}
        <input
          ref={ref}
          type="text"
          value={displayValue}
          onChange={handleChange}
          maxLength={maskConfig.maxLength}
          className={`
            w-full px-3 py-2 border rounded-lg shadow-sm
            focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500
            disabled:bg-gray-100 disabled:cursor-not-allowed
            ${error ? 'border-red-500' : 'border-gray-300'}
            ${className}
          `}
          {...props}
        />
        {error && (
          <p className="mt-1 text-sm text-red-600">{error}</p>
        )}
      </div>
    );
  }
);

InputMask.displayName = 'InputMask';
